<template>
  <b-modal
    ref="create-stock-transfer"
    size="xl"
    hide-footer
    title="Tạo lệnh yêu cầu điều hàng"
    @hidden="resetForm"
  >
    <b-row>
      <b-col md="6">
        <b-form-group>
          <label>Cửa hàng đích (kho nhận hàng) <span class="text-danger">*</span></label>
          <treeselect
            :options="storeOptions"
            :multiple="false"
            placeholder="Chọn cửa hàng đích (kho nhận)"
            noResultsText="Không có kết quả"
            :match-keys="['label']"
            openDirection="bottom"
            v-model="sourceStoreId"
          />
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group>
          <label>Cửa hàng nguồn (kho gửi hàng) <span class="text-danger">*</span></label>
          <treeselect
            :options="storeOptions"
            :multiple="true"
            placeholder="Chọn các cửa hàng nguồn (kho gửi)"
            noResultsText="Không có kết quả"
            :match-keys="['label']"
            openDirection="bottom"
            v-model="destStoreIds"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group>
          <label>Nội dung / Ghi chú <span class="text-danger">*</span></label>
          <b-form-textarea
            size="sm"
            placeholder="Mô tả nội dung điều hàng"
            :rows="2"
            :max-rows="4"
            v-model="summary"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <label>Sản phẩm cần điều <span class="text-danger">*</span></label>
        <ProductSearch :storeId="sourceStoreId" @selected="onSelectedProduct" />
      </b-col>
    </b-row>

    <b-row class="mt-3" v-if="products.length">
      <b-col>
        <b-table :items="products" :fields="productFields" bordered small>
          <template v-slot:cell(quantity)="row">
            <b-form-input
              type="number"
              size="sm"
              min="1"
              v-model.number="row.item.quantity"
              style="max-width: 100px"
            />
          </template>
          <template v-slot:cell(actions)="row">
            <i
              class="flaticon2-rubbish-bin-delete-button text-danger"
              style="cursor: pointer"
              @click="removeProduct(row.index)"
            ></i>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <!-- Ma trận tồn kho (FR-002): tham khảo, không chặn submit -->
    <b-row class="mt-3" v-if="products.length && destStoreIds.length">
      <b-col>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="mb-0">Tồn khả dụng theo cửa hàng (tham khảo)</label>
          <b-button size="sm" variant="light" @click="loadMatrix">
            <i class="fas fa-sync-alt"></i> Làm mới tồn
          </b-button>
        </div>
        <div style="overflow-x: auto">
          <table class="table table-bordered table-sm text-center mb-0">
            <thead>
              <tr>
                <th style="min-width: 200px; text-align: left">Sản phẩm</th>
                <th v-for="s in selectedStores" :key="s.id">{{ s.label }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in products" :key="p.productId">
                <td style="text-align: left">{{ p.productName }}</td>
                <td
                  v-for="s in selectedStores"
                  :key="s.id"
                  :class="cellClass(getAvailable(p.productId, s.id), p.quantity)"
                >
                  {{ formatCell(getAvailable(p.productId, s.id)) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <b-alert show variant="light" class="mt-2 mb-0" style="font-size: 12px">
          <i class="fas fa-info-circle"></i>
          Tồn chỉ mang tính tham khảo tại thời điểm tạo phiếu. Ô
          <span class="badge badge-danger">đỏ</span> = cửa hàng hết hàng —
          cân nhắc trước khi gửi (không bắt buộc).
        </b-alert>
      </b-col>
    </b-row>

    <b-button
      class="mt-4"
      variant="primary"
      block
      :disabled="onSaving"
      @click="onSubmit"
      >Lưu &amp; Gửi yêu cầu</b-button
    >
    <b-button class="mt-2" variant="outline-secondary" block @click="hide"
      >Đóng</b-button
    >
  </b-modal>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import debounce from 'lodash/debounce';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import ProductSearch from '@/view/components/products/ProductSearch.vue';
import { FUNCTION_ACTION_TICKET } from '@/utils/enum';

export default {
  components: { Treeselect, ProductSearch },
  data() {
    return {
      summary: '',
      sourceStoreId: null,
      destStoreIds: [],
      products: [],
      storeOptions: [],
      // matrix[productId][storeId] = availableNumber | null (null = chưa xác định)
      matrix: {},
      onSaving: false,
      productFields: [
        { key: 'productName', label: 'Tên sản phẩm' },
        { key: 'quantity', label: 'SL mỗi CH gửi', thStyle: { width: '140px' } },
        { key: 'actions', label: '', thStyle: { width: '50px' } },
      ],
    };
  },
  computed: {
    selectedStores() {
      return this.storeOptions.filter((s) => this.destStoreIds.includes(s.id));
    },
  },
  watch: {
    products: {
      handler() {
        this.debouncedLoadMatrix();
      },
      deep: true,
    },
    destStoreIds() {
      this.debouncedLoadMatrix();
    },
  },
  created() {
    this.fetchStores();
    this.debouncedLoadMatrix = debounce(this.loadMatrix, 300);
  },
  methods: {
    show() {
      this.$refs['create-stock-transfer'].show();
    },
    hide() {
      this.$refs['create-stock-transfer'].hide();
    },
    resetForm() {
      this.summary = '';
      this.sourceStoreId = null;
      this.destStoreIds = [];
      this.products = [];
      this.matrix = {};
    },
    async fetchStores() {
      ApiService.get('/stores/getStores')
        .then((response) => {
          const stores = (response.data && response.data.data) || [];
          this.storeOptions = stores.map((s) => ({
            id: s.id,
            label: s.name ? `${s.shortName} (${s.name})` : s.shortName || `#${s.id}`,
          }));
        })
        .catch(() => {
          makeToastFaile('Không tải được danh sách cửa hàng');
        });
    },
    onSelectedProduct(item) {
      const productId = item.productId || item.id;
      if (!productId) return;
      if (this.products.some((p) => p.productId === productId)) return;
      this.products.push({
        productId,
        productName: item.productName || item.label,
        quantity: 1,
      });
    },
    removeProduct(index) {
      this.products.splice(index, 1);
    },
    async loadMatrix() {
      if (!this.products.length || !this.destStoreIds.length) return;
      const newMatrix = {};
      // productStock trả listStockDetail KHÔNG kèm storeId (chỉ storeName), nên phải
      // gọi theo từng (sản phẩm x cửa hàng) và map theo storeId đã truyền vào.
      await Promise.all(
        this.products.flatMap((p) => {
          newMatrix[p.productId] = {};
          return this.destStoreIds.map(async (storeId) => {
            try {
              const resp = await ApiService.query('productStock', {
                params: { productId: p.productId, storeId },
              });
              const detail =
                resp.data && resp.data.data
                  ? resp.data.data.listStockDetail || []
                  : [];
              const row = detail[0];
              newMatrix[p.productId][storeId] = row
                ? (row.inStockQuantity || 0) - (row.holdingQuantity || 0)
                : null;
            } catch (e) {
              newMatrix[p.productId][storeId] = null;
            }
          });
        }),
      );
      this.matrix = newMatrix;
    },
    getAvailable(productId, storeId) {
      const byStore = this.matrix[productId];
      if (!byStore || byStore[storeId] === undefined) return null;
      return byStore[storeId];
    },
    formatCell(available) {
      return available === null ? '--' : available;
    },
    cellClass(available, needed) {
      if (available === null) return 'text-muted';
      if (available <= 0) return 'bg-danger text-white';
      if (available < needed) return 'bg-warning';
      return 'bg-success text-white';
    },
    validate() {
      if (!this.sourceStoreId) {
        makeToastFaile('Vui lòng chọn cửa hàng đích (kho nhận hàng)');
        return false;
      }
      if (!this.summary || !this.summary.trim()) {
        makeToastFaile('Vui lòng mô tả nội dung điều hàng');
        return false;
      }
      if (!this.products.length) {
        makeToastFaile('Vui lòng chọn ít nhất 1 sản phẩm');
        return false;
      }
      if (this.products.some((p) => !p.quantity || p.quantity < 1)) {
        makeToastFaile('Số lượng sản phẩm phải lớn hơn 0');
        return false;
      }
      if (!this.destStoreIds.length) {
        makeToastFaile('Vui lòng chọn ít nhất 1 cửa hàng nguồn (kho gửi hàng)');
        return false;
      }
      return true;
    },
    onSubmit() {
      if (this.onSaving) return;
      if (!this.validate()) return;
      this.onSaving = true;
      const payload = {
        type: FUNCTION_ACTION_TICKET.STOCK_TRANSFER,
        summary: this.summary.trim(),
        sourceStoreId: this.sourceStoreId,
        products: this.products.map((p) => ({
          productId: p.productId,
          quantity: p.quantity,
        })),
        storeIds: this.destStoreIds,
      };
      ApiService.post('/v2/tickets/create', payload)
        .then((response) => {
          const { status, message, data } = response.data;
          if (status === 200) {
            const count =
              data && data.splitCount ? data.splitCount : this.destStoreIds.length;
            makeToastSuccess(
              `Đã tạo và tách thành ${count} phiếu cho các cửa hàng nguồn được chọn.`,
            );
            this.hide();
            this.$emit('created');
          } else {
            makeToastFaile(message);
          }
        })
        .catch(({ response }) => {
          const message =
            response && response.data ? response.data.message : 'Có lỗi xảy ra';
          makeToastFaile(message);
        })
        .finally(() => {
          this.onSaving = false;
        });
    },
  },
};
</script>
